/**
 * useAnimeData Hook
 * アニメデータの状態管理とCRUD操作
 */

import { useState, useEffect, useCallback } from 'react'
import { parseRawData } from '../utils/parser'
import { fetchAnimeMetadata } from '../services/jikan'

// ローカルストレージキー
const STORAGE_KEY = 'anime_checker_data'

/**
 * APIが利用可能かチェック
 */
async function checkApiAvailable() {
  try {
    const response = await fetch('/api/health')
    return response.ok
  } catch {
    return false
  }
}

/**
 * アニメデータ管理フック
 */
export function useAnimeData() {
  const [animeList, setAnimeList] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [useApi, setUseApi] = useState(false)

  // 初期化：データを読み込む
  useEffect(() => {
    async function initialize() {
      setLoading(true)

      // APIが利用可能かチェック
      const apiAvailable = await checkApiAvailable()
      setUseApi(apiAvailable)

      if (apiAvailable) {
        await loadFromApi()
      } else {
        loadFromLocalStorage()
      }

      setLoading(false)
    }

    initialize()
  }, [])

  // データが変更されたらローカルストレージに保存
  useEffect(() => {
    if (!loading && !useApi) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(animeList))
    }
  }, [animeList, loading, useApi])

  /**
   * ローカルストレージからデータを読み込む
   */
  const loadFromLocalStorage = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        setAnimeList(Array.isArray(parsed) ? parsed : [])
      }
    } catch (err) {
      console.error('Failed to load from localStorage:', err)
      setError('データの読み込みに失敗しました')
    }
  }, [])

  /**
   * APIからデータを読み込む
   */
  const loadFromApi = useCallback(async () => {
    try {
      const response = await fetch('/api/anime')
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`)
      }
      const data = await response.json()
      setAnimeList(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error('Failed to load from API:', err)
      // APIが失敗したらローカルストレージにフォールバック
      setUseApi(false)
      loadFromLocalStorage()
    }
  }, [loadFromLocalStorage])

  /**
   * 生データをインポート
   */
  const importRawData = useCallback(async (rawText) => {
    try {
      const entries = parseRawData(rawText)

      if (entries.length === 0) {
        throw new Error('有効なデータが見つかりませんでした')
      }

      // 既存データとマージ（タイトルで重複チェック）
      const existingTitles = new Set(animeList.map(a => a.title.toLowerCase()))
      const newEntries = entries.filter(
        e => !existingTitles.has(e.title.toLowerCase())
      )
      const duplicateCount = entries.length - newEntries.length

      const updated = [...animeList, ...newEntries]
      setAnimeList(updated)

      if (useApi) {
        // APIに保存
        for (const entry of newEntries) {
          await fetch('/api/anime', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(entry)
          })
        }
      }

      return {
        imported: newEntries.length,
        duplicates: duplicateCount,
        total: updated.length
      }
    } catch (err) {
      console.error('Import failed:', err)
      setError(err.message)
      throw err
    }
  }, [animeList, useApi])

  /**
   * アニメを追加
   */
  const addAnime = useCallback(async (anime) => {
    const newAnime = {
      ...anime,
      id: anime.id || `anime_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      lastUpdated: new Date().toISOString()
    }

    // 即座にローカル状態を更新
    setAnimeList(prev => {
      const updated = [...prev, newAnime]
      console.log('Added anime to list:', newAnime.title, 'Total:', updated.length)
      return updated
    })

    // APIに保存
    if (useApi) {
      try {
        const response = await fetch('/api/anime', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newAnime)
        })
        if (!response.ok) {
          const errorText = await response.text()
          console.error('API save error:', response.status, errorText)
        } else {
          console.log('Saved to API successfully')
        }
      } catch (err) {
        console.error('Failed to save to API:', err)
      }
    }

    return newAnime
  }, [useApi])

  /**
   * アニメを更新
   */
  const updateAnime = useCallback(async (id, updates) => {
    const updatedData = {
      ...updates,
      lastUpdated: new Date().toISOString()
    }

    setAnimeList(prev =>
      prev.map(anime =>
        anime.id === id ? { ...anime, ...updatedData } : anime
      )
    )

    if (useApi) {
      try {
        await fetch(`/api/anime/${id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updatedData)
        })
      } catch (err) {
        console.error('Failed to update API:', err)
      }
    }
  }, [useApi])

  /**
   * エピソードを増加
   */
  const incrementEpisode = useCallback(async (id) => {
    const anime = animeList.find(a => a.id === id)
    if (!anime) return

    const newEpisode = (anime.currentEpisode || 0) + 1
    const updates = {
      currentEpisode: newEpisode,
      status: 'watching'
    }

    // 総エピソード数が分かっていて、完了した場合
    if (anime.totalEpisodes && newEpisode >= anime.totalEpisodes) {
      updates.status = 'completed'
    }

    await updateAnime(id, updates)
  }, [animeList, updateAnime])

  /**
   * アニメをドロップ
   */
  const dropAnime = useCallback(async (id, reason = '') => {
    await updateAnime(id, {
      status: 'dropped',
      dropReason: reason
    })
  }, [updateAnime])

  /**
   * アニメをスキップ
   */
  const skipAnime = useCallback(async (id, reason = '') => {
    await updateAnime(id, {
      status: 'skipped',
      dropReason: reason
    })
  }, [updateAnime])

  /**
   * DAVEスコアを更新
   */
  const updateDaveScore = useCallback(async (id, scoreDetail) => {
    const total = Object.values(scoreDetail).reduce((sum, v) => sum + v, 0)

    await updateAnime(id, {
      daveScoreDetail: scoreDetail,
      daveScore: total
    })
  }, [updateAnime])

  /**
   * アニメを削除
   */
  const deleteAnime = useCallback(async (id) => {
    setAnimeList(prev => prev.filter(anime => anime.id !== id))

    if (useApi) {
      try {
        await fetch(`/api/anime/${id}`, { method: 'DELETE' })
      } catch (err) {
        console.error('Failed to delete from API:', err)
      }
    }
  }, [useApi])

  /**
   * すべてのデータをクリア
   */
  const clearAll = useCallback(() => {
    setAnimeList([])
    localStorage.removeItem(STORAGE_KEY)
  }, [])

  /**
   * データをエクスポート
   */
  const exportData = useCallback(() => {
    return JSON.stringify(animeList, null, 2)
  }, [animeList])

  /**
   * JSONデータをインポート
   */
  const importJsonData = useCallback((jsonString) => {
    try {
      const data = JSON.parse(jsonString)
      if (!Array.isArray(data)) {
        throw new Error('Invalid data format')
      }
      setAnimeList(data)
      return { imported: data.length }
    } catch (err) {
      console.error('JSON import failed:', err)
      throw err
    }
  }, [])
 
   /**
    * メタデータを同期（画像・タイトルの取得など）
    */
   const syncMetadata = useCallback(async () => {
     let updatedCount = 0

     try {
       const newAnimeList = [...animeList]

       for (let i = 0; i < newAnimeList.length; i++) {
         const anime = newAnimeList[i]
         // メタデータがない、または画像がない場合に取得
         if (!anime.metadata || !anime.metadata.imageUrl) {
           try {
             const metadata = await fetchAnimeMetadata(anime.title)
             if (metadata) {
               // 正式タイトルを取得（日本語があれば優先、なければ英語）
               const officialTitle = metadata.titleJapanese || metadata.title || anime.title

               newAnimeList[i] = {
                 ...anime,
                 // 正式タイトルで上書き
                 title: officialTitle,
                 // 元のタイトルを保存（検索用）
                 originalTitle: anime.title,
                 metadata,
                 imageUrl: metadata.imageUrl,
                 totalEpisodes: metadata.episodes || anime.totalEpisodes,
                 airingStatus: metadata.airing ? 'currently_airing' : 'finished',
                 lastUpdated: new Date().toISOString()
               }
               updatedCount++

               // API更新
               if (useApi) {
                 await fetch(`/api/anime/${anime.id}`, {
                   method: 'PUT',
                   headers: { 'Content-Type': 'application/json' },
                   body: JSON.stringify(newAnimeList[i])
                 })
               }

               // 少し待機（レート制限に優しく）
               await new Promise(resolve => setTimeout(resolve, 500))
             }
           } catch (e) {
             console.error(`Failed to sync metadata for ${anime.title}`, e)
           }
         }
       }

       if (updatedCount > 0) {
         setAnimeList(newAnimeList)
       }

       return { updated: updatedCount }
     } finally {
       // noop
     }
   }, [animeList, useApi])

  /**
   * ローカルデータをクラウド（D1）へ移行
   */
  const migrateToCloud = useCallback(async () => {
    // 本番APIのベースURL
    const PROD_API = 'https://anime-checker-1ce.pages.dev'

    // 本番APIが利用可能かチェック
    try {
      const healthCheck = await fetch(`${PROD_API}/api/health`)
      if (!healthCheck.ok) {
        throw new Error('クラウドAPIに接続できません')
      }
    } catch {
      throw new Error('クラウドAPIに接続できません')
    }

    let migratedCount = 0
    let errorCount = 0

    for (const anime of animeList) {
      try {
        const response = await fetch(`${PROD_API}/api/anime`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(anime)
        })

        if (response.ok) {
          migratedCount++
        } else {
          // 既に存在する場合はPUTで更新
          const putResponse = await fetch(`${PROD_API}/api/anime/${anime.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(anime)
          })
          if (putResponse.ok) {
            migratedCount++
          } else {
            errorCount++
          }
        }
      } catch (err) {
        console.error(`Failed to migrate: ${anime.title}`, err)
        errorCount++
      }
    }

    return { migrated: migratedCount, errors: errorCount }
  }, [animeList])

  // ステータスでフィルタリングしたリスト
  const watchingList = animeList.filter(a => a.status === 'watching')
  const planToWatchList = animeList.filter(a => a.status === 'plan_to_watch')
  const completedList = animeList.filter(a => a.status === 'completed')
  const droppedList = animeList.filter(a => a.status === 'dropped' || a.status === 'skipped')

  return {
    animeList,
    watchingList,
    planToWatchList,
    completedList,
    droppedList,
    loading,
    error,
    useApi,
    importRawData,
    addAnime,
    updateAnime,
    incrementEpisode,
    dropAnime,
    skipAnime,
    updateDaveScore,
    deleteAnime,
    clearAll,
    exportData,
    importJsonData,
    syncMetadata,
    migrateToCloud,
    reload: useApi ? loadFromApi : loadFromLocalStorage
  }
}

export default useAnimeData
